import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import vert from "./shaders/player.vert.glsl";
import frag from "./shaders/drone.frag.glsl";
import type { ShowBuffers, ShowMeta } from "./showFormat";
import { showBuffers } from "./showBuffers";

type Props = {
  show: ShowMeta;
  playing: boolean;
  speed: number;
  playhead: React.MutableRefObject<number>;
  sliderRef: React.RefObject<HTMLInputElement | null>;
  brightness?: number;
  size?: number;
};

/**
 * Plays a decoded real show. Advances a normalized playhead and samples the
 * position and color tracks (each at its own rate) with linear interpolation,
 * writing straight into the geometry attributes every frame.
 */
export function ShowPlayer({
  show,
  playing,
  speed,
  playhead,
  sliderRef,
  brightness = 1.35,
  size = 8,
}: Props) {
  const matRef = useRef<THREE.ShaderMaterial>(null);
  // The heavy tracks live off-React (showBuffers). Capture them in a ref so the
  // per-frame loop reads them without them ever being props/state.
  const bufRef = useRef<ShowBuffers | null>(showBuffers.get());

  const geometry = useMemo(() => {
    const buf = showBuffers.get();
    bufRef.current = buf;
    const { droneCount } = show;
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(droneCount * 3);
    const col = new Float32Array(droneCount * 3);
    if (buf) {
      pos.set(buf.posScene.subarray(0, droneCount * 3));
      for (let i = 0; i < droneCount * 3; i++) col[i] = buf.colRGB[i] / 255;
    }
    const seed = new Float32Array(droneCount);
    for (let d = 0; d < droneCount; d++) seed[d] = Math.random();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.setAttribute("aColor", new THREE.BufferAttribute(col, 3));
    g.setAttribute("aSeed", new THREE.BufferAttribute(seed, 1));
    return g;
  }, [show]);

  useEffect(() => () => geometry.dispose(), [geometry]);

  const uniforms = useMemo(
    () => ({ uTime: { value: 0 }, uSize: { value: size }, uBrightness: { value: brightness } }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(() => {
    if (!matRef.current) return;
    matRef.current.uniforms.uSize.value = size;
    matRef.current.uniforms.uBrightness.value = brightness;
  }, [size, brightness]);

  useFrame((_, dt) => {
    const m = matRef.current;
    if (!m) return;
    m.uniforms.uTime.value += dt;
    const buf = bufRef.current;
    if (!buf) return;

    const dur = show.durationSec;
    if (playing) {
      playhead.current += dt * speed;
      if (playhead.current > dur) playhead.current -= dur;
      if (playhead.current < 0) playhead.current += dur;
    }
    const s = sliderRef.current;
    if (s && document.activeElement !== s) s.value = String(playhead.current);

    const t = Math.min(Math.max(playhead.current / dur, 0), 1);
    const { droneCount, Fp, Fc } = show;
    const { posScene, colRGB } = buf;
    const L = droneCount * 3;

    const posAttr = geometry.getAttribute("position") as THREE.BufferAttribute;
    const colAttr = geometry.getAttribute("aColor") as THREE.BufferAttribute;
    const pa = posAttr.array as Float32Array;
    const ca = colAttr.array as Float32Array;

    // position track
    const ft = t * (Fp - 1);
    const f0 = Math.floor(ft), f1 = Math.min(f0 + 1, Fp - 1), af = ft - f0;
    const pb0 = f0 * L, pb1 = f1 * L;
    for (let i = 0; i < L; i++) pa[i] = posScene[pb0 + i] + (posScene[pb1 + i] - posScene[pb0 + i]) * af;
    posAttr.needsUpdate = true;

    // color track (independent rate)
    const ct = t * (Fc - 1);
    const c0 = Math.floor(ct), c1 = Math.min(c0 + 1, Fc - 1), ac = ct - c0;
    const cb0 = c0 * L, cb1 = c1 * L;
    for (let i = 0; i < L; i++) {
      const a = colRGB[cb0 + i] / 255;
      const b = colRGB[cb1 + i] / 255;
      ca[i] = a + (b - a) * ac;
    }
    colAttr.needsUpdate = true;
  });

  return (
    <points name="swarm" geometry={geometry} frustumCulled={false}>
      <shaderMaterial
        ref={matRef}
        vertexShader={vert}
        fragmentShader={frag}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        depthTest={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
